import { Controller } from "@hotwired/stimulus"

// Nombre minimum d'options dans le formulaire de creation
const MIN_OPTIONS = 2
// Duree d'affichage du feedback d'erreur (ms)
const ERROR_FEEDBACK_MS = 2000

// Formulaire de creation d'un quiz de session live/presentiel (formateur).
// Issu de la decomposition de live_session_quiz_controller (787 l., Lot 7.6) :
// ce controller ne connait que le formulaire — la liste des quiz vit dans
// trainer-quiz-board, qui recoit les evenements du canal de session.
export default class extends Controller {
  static targets = ["optionsContainer", "submitBtn", "errorMessage"]
  static values = {
    errorQuestion: { type: String, default: "Veuillez saisir une question" },
    errorMinOptions: { type: String, default: "Ajoutez au moins 2 options" },
    errorNoCorrect: { type: String, default: "Cochez au moins une réponse correcte" },
    errorServer: { type: String, default: "Erreur lors de la création du quiz" }
  }

  // --- Ajout d'une option au formulaire ---

  addOption() {
    if (!this.hasOptionsContainerTarget) return

    const container = this.optionsContainerTarget
    const index = container.querySelectorAll("[data-option-row]").length
    // Construire la ligne d'option via DOM API (securise)
    const row = this.createOptionRowElement(index)
    container.appendChild(row)
    row.querySelector("input[type='text']").focus()
  }

  // --- Suppression d'une option ---

  removeOption(event) {
    event.preventDefault()
    if (!this.hasOptionsContainerTarget) return

    const rows = this.optionsContainerTarget.querySelectorAll("[data-option-row]")
    if (rows.length <= MIN_OPTIONS) return

    const row = event.currentTarget.closest("[data-option-row]")
    if (row) {
      row.remove()
      this.reindexOptions()
    }
  }

  // --- Creation d'un quiz via fetch ---

  async createQuiz(event) {
    event.preventDefault()
    this.hideError()

    const form = event.currentTarget
    const questionInput = form.querySelector('[name="quiz[question]"]')
    const question = questionInput.value.trim()

    if (!question) {
      this.showError(this.errorQuestionValue)
      return
    }

    const allowMultiple = form.querySelector('[name="quiz[allow_multiple]"]')?.checked || false

    const options = []
    this.optionsContainerTarget.querySelectorAll("[data-option-row]").forEach(row => {
      const text = row.querySelector("input[type='text']").value.trim()
      const correct = row.querySelector("input[type='checkbox']").checked
      if (text) options.push({ text, correct })
    })

    if (options.length < MIN_OPTIONS) {
      this.showError(this.errorMinOptionsValue)
      return
    }

    if (!options.some(o => o.correct)) {
      this.showError(this.errorNoCorrectValue)
      return
    }

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    // Formater les options en hash indexe (format attendu par Rails strong params)
    const indexedOptions = {}
    options.forEach((opt, i) => {
      indexedOptions[i] = { text: opt.text, correct: opt.correct }
    })

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          interactive_session_quiz: { question, allow_multiple: allowMultiple, options: indexedOptions }
        })
      })

      if (response.ok) {
        form.reset()
        this.resetOptionsForm()
      } else {
        this.showError(this.errorServerValue)
      }
    } catch (error) {
      console.error("[Quiz] Create error:", error)
      this.showError(this.errorServerValue)
    }
  }

  // --- Helpers du formulaire ---

  createOptionRowElement(index) {
    const row = document.createElement("div")
    row.dataset.optionRow = ""
    row.className = "flex items-center gap-2"

    const textInput = document.createElement("input")
    textInput.type = "text"
    textInput.placeholder = `Option ${index + 1}`
    textInput.className = "flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"

    const label = document.createElement("label")
    label.className = "flex items-center gap-1 text-xs text-gray-600 cursor-pointer whitespace-nowrap"

    const checkbox = document.createElement("input")
    checkbox.type = "checkbox"
    checkbox.className = "rounded border-gray-300 text-blue-600 focus:ring-blue-500"

    label.appendChild(checkbox)
    label.appendChild(document.createTextNode(" Correct"))

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "text-gray-400 hover:text-red-500 transition-colors"
    removeBtn.dataset.action = "click->quiz-composer#removeOption"

    const removeIcon = document.createElement("i")
    removeIcon.className = "fas fa-times text-xs"
    removeBtn.appendChild(removeIcon)

    row.appendChild(textInput)
    row.appendChild(label)
    row.appendChild(removeBtn)

    return row
  }

  resetOptionsForm() {
    if (!this.hasOptionsContainerTarget) return

    const container = this.optionsContainerTarget
    container.replaceChildren()
    for (let i = 0; i < MIN_OPTIONS; i++) {
      container.appendChild(this.createOptionRowElement(i))
    }
  }

  reindexOptions() {
    if (!this.hasOptionsContainerTarget) return

    this.optionsContainerTarget.querySelectorAll("[data-option-row]").forEach((row, index) => {
      const textInput = row.querySelector("input[type='text']")
      if (textInput) textInput.placeholder = `Option ${index + 1}`
    })
  }

  // --- Feedback d'erreur ---

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
    if (this.hasSubmitBtnTarget) {
      this.flashError(this.submitBtnTarget)
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
      this.errorMessageTarget.textContent = ""
    }
  }

  flashError(element) {
    element.classList.add("ring-2", "ring-red-500")
    setTimeout(() => {
      element.classList.remove("ring-2", "ring-red-500")
    }, ERROR_FEEDBACK_MS)
  }
}
